"use client";

import React from "react";
import { useRouter } from "next/navigation";
import type { Product as ApiProduct } from "@/src/types/tenant";
import { useCart } from "@/src/contexts/CartContext";
import { useAuth } from "@/src/contexts/AuthContext";
import { addProductToCart, removeProduct, deleteCart } from "@/src/services/cart.service";
import { CountdownTimer } from "./CountdownTimer";

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  outletId: string;
  discountPct?: number;
  isOffer?: boolean;
  category?: string;
  weight?: string;
  tag?: string;
  stock?: number;
  offerStartTime?: string;
  offerEndTime?: string;
}

/** Returns true if current time is between start and end */
function isOfferActive(start?: string, end?: string): boolean {
  if (!start || !end) return false;
  const now = Date.now();
  return now >= new Date(start).getTime() && now <= new Date(end).getTime();
}

interface AllProductsProps {
  products?: ApiProduct[];
  outletId?: string;
  sortBy?: string;
  prefix?: string;
  limit?: number;
  showViewAll?: boolean;
}

function getCurrentPrice(p: ApiProduct) {
  return p.pricing?.offerPrice ?? p.pricing?.sellingPrice ?? 0;
}

function mapProduct(p: ApiProduct, fallbackOutletId: string): Product {
  const price = getCurrentPrice(p);
  const mrp = p.pricing?.mrp ?? 0;
  const originalPrice =
    mrp > price ? mrp
    : p.pricing?.sellingPrice && p.pricing.sellingPrice > price
      ? p.pricing.sellingPrice
      : undefined;
  const discountPct =
    originalPrice && originalPrice > 0
      ? Math.round(((originalPrice - price) / originalPrice) * 100)
      : undefined;

  return {
    id: p._id,
    name: p.name,
    price,
    originalPrice,
    discountPct,
    image: p.images?.thumbnail || "",
    outletId: p.outlet?.outletId || fallbackOutletId,
    isOffer: !!p.pricing?.offerPrice,
    category: p.category?.name,
    stock: p.inventory?.quantity,
    offerStartTime: p.pricing?.offerStartTime,
    offerEndTime: p.pricing?.offerEndTime,
  };
}

export const AllProducts: React.FC<AllProductsProps> = ({
  products = [],
  outletId = "",
  sortBy = "curator",
  prefix = "",
  limit,
  showViewAll = true,
}) => {
  const router = useRouter();
  const { addItem, updateQuantity, removeItem, getItemQuantity, items } = useCart();
  const { isAuthenticated, userId } = useAuth();
  const [activeSortBy, setActiveSortBy] = React.useState(sortBy);

  const productList = React.useMemo(() => {
  let mapped = products.map((p) => mapProduct(p, outletId));

  if (activeSortBy === "price-low") {
    mapped = [...mapped].sort((a, b) => a.price - b.price);
  } else if (activeSortBy === "price-high") {
    mapped = [...mapped].sort((a, b) => b.price - a.price);
  } else if (activeSortBy === "newest") {
    mapped = [...mapped].reverse();
  }

  return limit ? mapped.slice(0, limit) : mapped;
}, [products, outletId, activeSortBy, limit]);

  const handleAdd = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    const isDifferentOutlet = items.length > 0 && items[0].outletId !== product.outletId;
    addItem({
      productId: product.id,
      outletId: product.outletId,
      name: product.name,
      price: product.price,
      mrp: product.originalPrice,
      quantity: 1,
      image: product.image,
    });
    if (isAuthenticated && userId) {
      (async () => {
        if (isDifferentOutlet) {
          try { await deleteCart(userId); } catch { /* non-fatal */ }
        }
        addProductToCart(userId, product.outletId, product.id, 1).catch(() => {});
      })();
    }
  };

  const handleQty = (product: Product, qty: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (qty <= 0) {
      removeItem(product.id);
      if (isAuthenticated && userId) {
        removeProduct(userId, product.id).catch(() => {});
      }
    } else {
      const current = getItemQuantity(product.id);
      updateQuantity(product.id, qty);
      if (isAuthenticated && userId) {
        if (qty > current) {
          addProductToCart(userId, product.outletId, product.id, 1).catch(() => {});
        } else {
          removeProduct(userId, product.id).catch(() => {});
        }
      }
    }
  };

  return (
    <section className="w-full">
      {/* Grid header */}
      <div
        className="mb-6 flex flex-wrap items-end justify-between gap-4 pb-5"
        style={{ borderBottom: "1px solid var(--border-soft)" }}
      >
        <div>
          <h2
            className="font-bold leading-tight"
            style={{
              fontSize: "clamp(22px,2.6vw,28px)",
              letterSpacing: "-0.6px",
              color: "var(--techno-black)",
            }}
          >
            Products
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--techno-black-50)" }}>
            Showing {productList.length} picks
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <select
            value={activeSortBy}
            onChange={(e) => setActiveSortBy(e.target.value)}
            className="flex cursor-pointer items-center gap-2 rounded-[10px] border px-3.5 py-2.5 text-sm font-medium outline-none"
            style={{
              background: "var(--surface)",
              borderColor: "var(--border)",
              color: "var(--techno-black)",
              appearance: "none",
              paddingRight: "2rem",
            }}
          >
            <option value="curator">Sort: Curator&apos;s pick</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="newest">Newest First</option>
          </select>
         
         {showViewAll && products.length > (limit || 0) && (
  <button
    onClick={() => router.push(`/shop/all-products?prefix=${prefix}`)}
            className="flex shrink-0 items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-70"
            style={{ color: "var(--techno-black-70)" }}
            >
            View all
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
              className="transition-transform"
              style={{ transform: "none" }}
              >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
         )}
              </div>
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-2 gap-[clamp(12px,1.6vw,20px)] lg:grid-cols-3 xl:grid-cols-4">
        {productList.map((product) => {
          const cartQty = getItemQuantity(product.id);
          const hasImage = product.image.startsWith("http");
          const isLowStock = product.stock !== undefined && product.stock > 0 && product.stock <= 5;

          return (
            <div
              key={product.id}
              onClick={() =>
                router.push(
                  `/shop/product/${product.id}?prefix=${prefix}&outletId=${outletId}`
                )
              }
              className="group cursor-pointer"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 16,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 12px 32px rgba(40,30,20,0.08)";
                e.currentTarget.style.borderColor = "var(--border)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.borderColor = "var(--border-soft)";
              }}
            >
              {/* Image area */}
              <div
                className="relative overflow-hidden"
                style={{
                  aspectRatio: "1",
                  background: "linear-gradient(135deg, #F4F1EA 0%, #ECE7DC 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {hasImage ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                  />
                ) : (
                  <span
                    className="font-extrabold"
                    style={{ fontSize: "clamp(32px,5vw,48px)", color: "rgba(53,73,48,0.12)" }}
                  >
                    {product.name.slice(0, 2).toUpperCase()}
                  </span>
                )}

                {/* Badges */}
                <div className="absolute left-2.5 top-2.5 flex flex-col gap-1 items-start">
                  {product.isOffer && (
                    <span
                      className="rounded-[6px] px-2 py-1 text-[10.5px] font-bold uppercase tracking-[0.4px] text-white"
                      style={{ background: "var(--rising-sun)" }}
                    >
                      OFFER
                    </span>
                  )}
                  {!product.isOffer && product.discountPct && product.discountPct >= 5 && (
                    <span
                      className="rounded-[6px] px-2 py-1 text-[10.5px] font-bold text-white"
                      style={{ background: "#E4604E" }}
                    >
                      {product.discountPct}% off
                    </span>
                  )}
                </div>

                {/* Quick view button (hover only) */}
                <button
                  onClick={(e) => { e.stopPropagation(); router.push(`/shop/product/${product.id}?prefix=${prefix}&outletId=${outletId}`); }}
                  className="absolute right-2.5 top-2.5 flex h-[34px] w-[34px] items-center justify-center rounded-full opacity-0 transition-all group-hover:opacity-100"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(10px)",
                    color: "var(--techno-black)",
                    transform: "translateY(-4px)",
                  }}
                  title="Quick view"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>

                {/* Low stock strip */}
                {isLowStock && (
                  <div
                    className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-1.5 py-1 text-[11px] font-semibold text-white"
                    style={{ background: "linear-gradient(90deg, rgba(228,96,78,0.95), rgba(228,96,78,0.85))" }}
                  >
                    <span
                      className="rounded-full"
                      style={{ width: 6, height: 6, background: "#fff", animation: "blip 1s ease-in-out infinite", display: "inline-block" }}
                    />
                    Only {product.stock} left
                  </div>
                )}
              </div>

              {/* Product info */}
              <div className="flex flex-1 flex-col gap-1 p-3.5 pb-4">
                {product.category && (
                  <span
                    className="self-start rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{
                      background: "var(--border-soft)",
                      color: "var(--techno-black-50)",
                    }}
                  >
                    {product.category}
                  </span>
                )}

                <h3
                  className="line-clamp-2 leading-snug"
                  style={{
                    fontSize: "14.5px",
                    fontWeight: 600,
                    letterSpacing: "-0.2px",
                    color: "var(--techno-black)",
                    minHeight: 38,
                  }}
                >
                  {product.name}
                </h3>

                {/* Mini countdown — only when offer window is active */}
                {isOfferActive(product.offerStartTime, product.offerEndTime) && (
                  <div className="mt-1" onClick={e => e.stopPropagation()}>
                    <CountdownTimer mini endsAt={product.offerEndTime} />
                  </div>
                )}

                {/* Price */}
                <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <span
                      className="font-bold"
                      style={{ fontSize: 16, color: "var(--techno-black)" }}
                    >
                      ₹{product.price}
                    </span>
                    {product.originalPrice && (
                      <span
                        className="font-medium"
                        style={{ fontSize: "12.5px", color: "var(--techno-black-50)", textDecoration: "line-through" }}
                      >
                        ₹{product.originalPrice}
                      </span>
                    )}
                    {product.discountPct && product.discountPct >= 5 && (
                      <span
                        className="rounded-[4px] px-1.5 py-0.5 text-[11px] font-bold"
                        style={{ color: "#E4604E", background: "rgba(228,96,78,0.1)" }}
                      >
                        {product.discountPct}% off
                      </span>
                    )}
                  </div>

                  {/* Add / qty control */}
                  {cartQty > 0 ? (
                    <div
                      className="flex shrink-0 overflow-hidden rounded-lg"
                      style={{ height: 36, background: "#E4604E" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={(e) => handleQty(product, cartQty - 1, e)}
                        className="flex w-8 items-center justify-center text-lg font-light text-white transition hover:bg-black/15"
                      >
                        −
                      </button>
                      <span
                        className="flex min-w-[24px] items-center justify-center text-sm font-bold text-white"
                      >
                        {cartQty}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleQty(product, cartQty + 1, e)}
                        className="flex w-8 items-center justify-center text-lg font-light text-white transition hover:bg-black/15"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => handleAdd(product, e)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-bold transition active:scale-95"
                      style={{
                        border: "1.5px solid #E4604E",
                        background: "var(--surface)",
                        color: "#E4604E",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = "#E4604E";
                        e.currentTarget.style.color = "#fff";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = "var(--surface)";
                        e.currentTarget.style.color = "#E4604E";
                      }}
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
